/**
 * The demo project's id, which is also the segment in its URLs.
 *
 * A readable literal rather than a UUID because a demo link exists to be pasted
 * into a chat, and `/projects/demo/profiles/checkout-api-cpu-v1-9-0` says
 * what it is.
 */
export const DEMO_PROJECT_ID = 'demo'

/** True when a path addresses the demo project. */
export function isDemoPath(path: string): boolean {
  return path.startsWith(`/projects/${DEMO_PROJECT_ID}`)
}

/** True when the browser is currently showing the demo. */
export function isDemoRoute(pathname: string): boolean {
  return pathname === '/demo' || pathname.startsWith(`/projects/${DEMO_PROJECT_ID}`)
}

type Responses = Record<string, unknown>

/**
 * The captured responses, fetched the first time somebody opens the demo.
 *
 * Deliberately a dynamic import. This module is reached from the API client and
 * from the app shell, so a static import would put the whole demo — every flame
 * graph of a service nobody here runs — into the bundle that serves the landing
 * page. That is the one page whose speed the product is advertising, and the
 * visitor it is being advertised to has not asked for the demo yet.
 */
let pending: Promise<Responses> | null = null

/**
 * An ISO 8601 instant, as this API writes them.
 *
 * Deliberately anchored and strict. Every string in the document is tested
 * against it, and a loose pattern that also matched a version, a git sha or a
 * function name would rewrite it into a date.
 */
const INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/

/** How long ago the demo's newest profile should look. */
const NEWEST_PROFILE_AGE_MS = 4 * 60 * 1000

/**
 * Moves every captured moment forward so the demo is always current.
 *
 * The fixtures are frozen responses, and the times inside them are the times
 * things really happened — which ages badly in a way nothing here would notice.
 * A week after capture the newest profile is a week old, "last seen" says seven
 * days, and every window the service page offers below that is empty: the
 * default view of the demo becomes a blank chart, which is the one thing this
 * data exists to prevent.
 *
 * One shift for the whole document, never per response. Every interval, every
 * gap between two builds and every alignment between services survives exactly
 * as captured; only the anchor moves. Shifting responses independently would
 * quietly invent a history none of them had.
 */
function rebaseTimestamps(document: unknown): void {
  const slots: [Record<string, unknown>, string, number][] = []
  let newest = Number.NEGATIVE_INFINITY

  const visit = (node: unknown) => {
    if (node === null || typeof node !== 'object') {
      return
    }
    // Arrays and objects are both walked by key, which for an array is its
    // index — the assignment below works the same on either.
    const holder = node as Record<string, unknown>
    for (const key of Object.keys(holder)) {
      const child = holder[key]
      if (typeof child !== 'string') {
        visit(child)
        continue
      }
      if (!INSTANT.test(child)) {
        continue
      }
      const at = Date.parse(child)
      if (Number.isNaN(at)) {
        continue
      }
      slots.push([holder, key, at])
      newest = Math.max(newest, at)
    }
  }

  visit(document)
  if (slots.length === 0) {
    return
  }

  const shift = Date.now() - NEWEST_PROFILE_AGE_MS - newest
  for (const [holder, key, at] of slots) {
    holder[key] = new Date(at + shift).toISOString()
  }
}

function fixtures(): Promise<Responses> {
  // Imported as text and parsed at runtime, not as a module. Flame graphs are
  // deep trees, and asking the bundler to turn one into a JavaScript object
  // literal exhausts its recursion limit outright. JSON.parse has no such
  // trouble and is the faster of the two on a payload this size.
  pending ??= import('./fixtures.json?raw').then((module) => {
    const responses = JSON.parse(module.default) as Responses
    rebaseTimestamps(responses)
    return responses
  })
  return pending
}

/**
 * DemoNotFound is thrown for a demo path nothing was captured for.
 *
 * Loud on purpose. The alternative — returning an empty list — produces a demo
 * page that looks broken to a visitor and looks fine to us.
 */
export class DemoNotFound extends Error {
  constructor(path: string) {
    super(`the demo has no data for ${path}`)
    this.name = 'DemoNotFound'
  }
}

/**
 * resolve answers a demo request from the captured fixtures.
 *
 * The fixtures are real responses from the real API, so the demo renders
 * through exactly the same components and hooks as a signed-in account. Nothing
 * here reimplements the product; it only replaces the transport.
 */
export async function resolve<T>(path: string): Promise<T> {
  const responses = await fixtures()

  // The profile list is filtered and paginated by the caller, so it is computed
  // rather than looked up. Matching on the exact query string would mean every
  // new `limit` anywhere in the UI silently emptied a demo page — the failure
  // this whole file is trying not to have.
  const list = resolveProfileList(responses, path)
  if (list) {
    return list as T
  }

  const direct = responses[path]
  if (direct !== undefined) {
    return direct as T
  }

  // Query parameters arrive in whatever order the caller built them, and a
  // filter with every value empty is the same request as none at all.
  const normalised = normalise(path)
  for (const [key, value] of Object.entries(responses)) {
    if (normalise(key) === normalised) {
      return value as T
    }
  }

  throw new DemoNotFound(path)
}

/** The captured, unfiltered profile list — the source every page is cut from. */
interface DemoProfile {
  id: string
  service_id: string
  environment_id: string
  instance_id: string
  profile_type: string
}

interface DemoProfileList {
  items: DemoProfile[]
  total: number
  limit: number
  offset: number
}

function resolveProfileList(responses: Responses, path: string): DemoProfileList | null {
  const [base, search] = path.split('?')
  if (base !== `/projects/${DEMO_PROJECT_ID}/profiles`) {
    return null
  }

  const all = (responses[`/projects/${DEMO_PROJECT_ID}/profiles`] as DemoProfileList | undefined)
    ?.items
  if (!all) {
    return null
  }

  const params = new URLSearchParams(search ?? '')
  const serviceID = params.get('service_id')
  const environmentID = params.get('environment_id')
  const instanceID = params.get('instance_id')
  const profileType = params.get('profile_type')

  const matched = all.filter(
    (profile) =>
      (!serviceID || profile.service_id === serviceID) &&
      (!environmentID || profile.environment_id === environmentID) &&
      (!instanceID || profile.instance_id === instanceID) &&
      (!profileType || profile.profile_type === profileType),
  )

  const limit = Number(params.get('limit') ?? 0) || matched.length
  const offset = Number(params.get('offset') ?? 0) || 0

  return {
    items: matched.slice(offset, offset + limit),
    total: matched.length,
    limit,
    offset,
  }
}

/** normalise sorts query parameters and drops empty ones. */
function normalise(path: string): string {
  const [base, search] = path.split('?')
  if (!search) {
    return base
  }

  const params = Array.from(new URLSearchParams(search).entries())
    .filter(([, value]) => value !== '')
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}=${value}`)

  return params.length > 0 ? `${base}?${params.join('&')}` : base
}

/**
 * Actions the demo cannot perform.
 *
 * There is no backend behind the demo, so a write has nothing to write to —
 * read-only is structural here, not a permission somebody could get wrong. This
 * error exists to say so in words rather than as a failed fetch.
 */
export class DemoReadOnly extends Error {
  constructor() {
    super('The demo is read-only. Create an account to send your own profiles.')
    this.name = 'DemoReadOnly'
  }
}
